import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const BRAND = {
  bone: '#F0F0F0',
  cobalt: '#5A1DEF',
  midnight: '#1C143D',
  slate: '#404041',
  punch: '#E91A60',
  ice: '#A5C3B7',
} as const;

type UserPreview = { id: string; email: string; fullName: string };

type ValidationState =
  | { status: 'loading' }
  | { status: 'invalid'; reason: string }
  | { status: 'valid'; user: UserPreview; expiresAt: string };

type UnlockState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'done'; email: string }
  | { status: 'error'; reason: string };

const KNOWN_REASONS = new Set([
  'not_found',
  'already_used',
  'expired',
  'missing_token',
  'invalid_or_expired',
  'postgres_disabled',
]);

export function UnlockAccountPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  function reasonText(reason?: string) {
    if (!reason) return t('unlock.reason.generic');
    if (KNOWN_REASONS.has(reason)) return t(`unlock.reason.${reason}`);
    return t('unlock.reason.withCode', { code: reason });
  }

  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [validation, setValidation] = useState<ValidationState>({ status: 'loading' });
  const [unlock, setUnlock] = useState<UnlockState>({ status: 'idle' });

  useEffect(() => {
    let cancelled = false;
    async function validate() {
      if (!token) {
        if (!cancelled) setValidation({ status: 'invalid', reason: 'missing_token' });
        return;
      }
      try {
        const response = await fetch(`/api/v4/auth/unlock/validate?token=${encodeURIComponent(token)}`, {
          method: 'GET',
          credentials: 'include',
        });
        const data = await response.json();
        if (cancelled) return;
        if (!response.ok || !data.valid) {
          setValidation({ status: 'invalid', reason: data.reason || 'invalid_token' });
          return;
        }
        setValidation({ status: 'valid', user: data.user, expiresAt: data.expiresAt });
      } catch {
        if (!cancelled) setValidation({ status: 'invalid', reason: 'network_error' });
      }
    }
    validate();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleUnlock() {
    if (validation.status !== 'valid') return;
    setUnlock({ status: 'submitting' });
    try {
      const response = await fetch('/api/v4/auth/unlock', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token }),
      });
      const data = await response.json();
      if (!response.ok || !data.ok) {
        setUnlock({ status: 'error', reason: data.error || 'unlock_failed' });
        return;
      }
      setUnlock({ status: 'done', email: data.email });
    } catch {
      setUnlock({ status: 'error', reason: 'network_error' });
    }
  }

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4"
      style={{ backgroundColor: BRAND.bone }}
    >
      <div className="mb-8 text-center">
        <h1
          className="text-4xl font-light tracking-[0.3em] uppercase mb-2"
          style={{ color: BRAND.midnight, fontFamily: "'Work Sans', sans-serif" }}
        >
          ATEK
        </h1>
        <p className="text-sm tracking-wide" style={{ color: BRAND.slate }}>
          {t('unlock.title')}
        </p>
      </div>

      <Card className="w-full max-w-md shadow-lg border-0">
        <CardContent className="pt-8 pb-8 px-8 space-y-6">
          {validation.status === 'loading' && (
            <p className="text-center text-sm" style={{ color: BRAND.slate }}>
              {t('unlock.verifyingLink')}
            </p>
          )}

          {validation.status === 'invalid' && (
            <div className="space-y-4 text-center">
              <h2 className="text-lg font-semibold" style={{ color: BRAND.punch }}>
                {t('unlock.linkUnavailable')}
              </h2>
              <p className="text-sm" style={{ color: BRAND.slate }}>
                {reasonText(validation.reason)}
              </p>
              <Button variant="outline" onClick={() => navigate('/login')}>
                {t('unlock.goToLogin')}
              </Button>
            </div>
          )}

          {validation.status === 'valid' && unlock.status !== 'done' && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold" style={{ color: BRAND.midnight }}>
                {t('unlock.unlockAccount')}
              </h2>
              <p className="text-sm" style={{ color: BRAND.slate }}>
                {t('unlock.aboutToRestore')}
              </p>
              <div
                className="rounded-md border p-4"
                style={{ borderColor: BRAND.ice, backgroundColor: '#fff' }}
              >
                <div className="font-medium" style={{ color: BRAND.midnight }}>
                  {validation.user.fullName}
                </div>
                <div className="text-xs" style={{ color: BRAND.slate }}>
                  {validation.user.email}
                </div>
              </div>
              <p className="text-xs" style={{ color: BRAND.slate }}>
                {t('unlock.consumeNotice')}
              </p>
              {unlock.status === 'error' && (
                <p className="text-xs" style={{ color: BRAND.punch }}>
                  {reasonText(unlock.reason)}
                </p>
              )}
              <Button
                className="w-full"
                style={{ backgroundColor: BRAND.cobalt, color: '#fff' }}
                onClick={handleUnlock}
                disabled={unlock.status === 'submitting'}
              >
                {unlock.status === 'submitting' ? t('unlock.unlocking') : t('unlock.confirmUnlock')}
              </Button>
            </div>
          )}

          {unlock.status === 'done' && (
            <div className="space-y-4 text-center">
              <h2 className="text-lg font-semibold" style={{ color: BRAND.midnight }}>
                {t('unlock.accountUnlocked')}
              </h2>
              <p className="text-sm" style={{ color: BRAND.slate }}>
                {t('unlock.canSignInAgain', { email: unlock.email })}
              </p>
              <Button onClick={() => navigate('/login')}>{t('unlock.goToLogin')}</Button>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="mt-8 text-xs" style={{ color: BRAND.slate }}>
        {t('auth.footerTagline')}
      </p>
    </div>
  );
}
